import type { AxiosInstance } from "axios";

/**
 * Who is signed in, and which stores they may open.
 *
 * THE STORE LIST COMES FROM THE SERVER ON EVERY LOAD, never from localStorage. Legacy
 * cached `selectedShop` in localStorage and trusted it; that is why a store revoked in
 * the backend stayed clickable in the browser until someone cleared their storage. Here
 * the list is `/auth/me`, resolved from `memberships` at request time — so a revoked
 * store disappears on the next page load, and a stale localStorage entry cannot resurrect
 * it because the id is validated server-side on every call anyway.
 *
 * WHAT localStorage IS STILL USED FOR: remembering WHICH of the returned stores was last
 * selected. That is a preference, not a permission — and if it names a store no longer in
 * the list, it is ignored rather than honoured.
 */

export interface SessionStore {
  readonly tenantId: string;
  readonly shopDomain: string;
  readonly displayName: string;
  readonly role: string;
}

export interface SessionUser {
  readonly id: string;
  readonly email: string;
  readonly name: string;
}

export interface Session {
  readonly user: SessionUser;
  readonly stores: readonly SessionStore[];
}

const SELECTED_STORE_KEY = "ecomally.selectedTenantId";

export async function fetchSession(client: AxiosInstance): Promise<Session> {
  const response = await client.get<{ success: boolean; data: Session }>("/auth/me");
  return response.data.data;
}

/**
 * The remembered choice, validated against what the server just said.
 *
 * Falls back to the first store rather than to nothing: a merchant with exactly one store
 * — which is every brand — should never be asked to pick it.
 */
export function resolveSelectedStore(stores: readonly SessionStore[]): SessionStore | null {
  if (stores.length === 0) return null;

  const remembered = readRemembered();
  const match = stores.find((store) => store.tenantId === remembered);
  return match ?? stores[0] ?? null;
}

export function rememberStore(tenantId: string): void {
  try {
    window.localStorage.setItem(SELECTED_STORE_KEY, tenantId);
  } catch (error) {
    /**
     * Safari in private mode throws on `setItem`. Losing a REMEMBERED PREFERENCE must
     * never break the dashboard — the app falls back to the first store and works.
     * (This is the one place a bare catch is right: there is no logger in a browser
     * bundle and a console line lands in a merchant's devtools — the same reasoning
     * .ai/02 already makes for packages/tracker.)
     */
    void error;
  }
}

function readRemembered(): string | null {
  try {
    return window.localStorage.getItem(SELECTED_STORE_KEY);
  } catch (error) {
    void error;
    return null;
  }
}
